import { QuoteItemRequestParams, QuoteGetResponse, QuoteItemsSearchResponse } from '@lio/api-types';
import { QuoteStatus } from '@models/quotes';
import { quoteItemQueryKey } from '@services/quoteItem/keys';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';

export const useDeleteQuoteItem = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  const mutationFn = ({ quoteId, quoteItemId }: QuoteItemRequestParams) =>
    client.delete(`quote/${quoteId}/quote-item/${quoteItemId}`, {
      json: {
        quoteId,
        quoteItemId,
      },
    });

  const revertToIncompleteStatuses = [QuoteStatus.ReadyToBind.id, QuoteStatus.Underwritten.id];

  const result = useMutation({
    mutationFn,
    onSuccess: (_data, variables) => {
      queryClient.setQueryData<QuoteGetResponse>(['quote', { quoteId: variables.quoteId }], (previousQuoteDetails) => {
        if (previousQuoteDetails && revertToIncompleteStatuses.includes(previousQuoteDetails?.status.id as string)) {
          return {
            ...(previousQuoteDetails ?? {}),
            status: {
              id: QuoteStatus.Rated.id,
              name: QuoteStatus.Rated.name,
              label: QuoteStatus.Rated.externalStatus,
            },
          };
        }
        return previousQuoteDetails;
      });
      queryClient.setQueryData<QuoteItemsSearchResponse>(
        quoteItemQueryKey.searchWithParams({
          quoteId: variables.quoteId,
        }),
        (previousQuoteItemSearch) => {
          const childQuoteItemIds = findChildQuoteItemIds(variables.quoteItemId, previousQuoteItemSearch?.items ?? []);
          return {
            ...previousQuoteItemSearch,
            items:
              previousQuoteItemSearch?.items.filter(
                (qi) => qi.id !== variables.quoteItemId && !childQuoteItemIds.includes(qi.id),
              ) ?? [],
          };
        },
      );
    },
  });

  function findChildQuoteItemIds(quoteItemId: string, quoteItems: QuoteItemsSearchResponse['items']): string[] {
    const children = quoteItems.filter((qi) => qi.parentId === quoteItemId);
    return children.flatMap((qi) => [qi.id, ...findChildQuoteItemIds(qi.id, quoteItems)]);
  }

  return { ...result, deleteQuoteItem: result.mutateAsync };
};
